import { FieldRoot } from "./field-root"
import { FieldItem } from "./field-item"
import { FieldLabel } from "./field-label"
import { FieldDescription } from "./field-description"

export type { FieldRootProps, FieldOrientation } from "./field-root"
export type { FieldItemProps } from "./field-item"
export type { FieldLabelProps } from "./field-label"
export type { FieldDescriptionProps } from "./field-description"

/**
 * Groups a form control with its label.
 *
 * The field owns what the two share: the control's id (so the label resolves
 * its own `htmlFor`), the `disabled` state (so the label greys out with the
 * control), and the layout between them.
 *
 * A group of controls (eg. a radiogroup) is still one field. Wrap each option in a `Field.Item`
 * and the group keeps one name and one validation state, while each option
 * gets its own label, description and `disabled`. The items need a group
 * wrapper around them to carry the group's role and name — a plain
 * `role="group"` element until `RadioGroup` and `CheckboxGroup` land.
 *
 * Any control built on Base UI registers itself with the field, so no `id`,
 * `htmlFor` or `aria-describedby` is passed at the call site.
 *
 * @example
 * ```tsx
 * <Field disabled={disabled}>
 *   <Field.Label>Reply-to address</Field.Label>
 *   <Input value={value} onValueChange={setValue} />
 * </Field>
 * ```
 *
 * @example A group of controls, one per `Field.Item`
 * ```tsx
 * <Field>
 *   <div role="group" aria-label="Email notifications" className="flex flex-col gap-2">
 *     <Field.Item>
 *       <Checkbox checked={broadcasts} onCheckedChange={setBroadcasts} />
 *       <Field.Label>Broadcasts</Field.Label>
 *       <Field.Description>One-off emails to a segment of your subscribers.</Field.Description>
 *     </Field.Item>
 *     <Field.Item>
 *       <Checkbox checked={sequences} onCheckedChange={setSequences} />
 *       <Field.Label>Sequences</Field.Label>
 *     </Field.Item>
 *   </div>
 * </Field>
 * ```
 */
export const Field = Object.assign(FieldRoot, {
  Item: FieldItem,
  Label: FieldLabel,
  Description: FieldDescription
} as const)
